<template>
    <div class="border-neutral-200 dark:border-neutral-700 flex items-center"
         data-cy="exercise-row"
         :class="[
           bordered ? 'my-2 border rounded p-3' : compact ? 'px-3 py-2 text-sm' : 'my-2 border-t first:border-t-0 p-2',
         ]"
    >

        <div class="min-w-0 flex-1">

            <template v-if="compact">
                <div class="flex min-h-8 items-center justify-between gap-3">
                    <div class="min-w-0">
                        <div class="flex min-w-0 items-center gap-x-2.5">
                            <div class="min-w-0" data-cy="exercise-resistance-summary" v-if="isBandResistance">
                                <template v-if="exerciseBands.length">
                                    <div data-cy="exercise-resistance-band" v-for="band in exerciseBands" :key="band.id">
                                        {{ band.brandAndName }}
                                    </div>
                                </template>
                                <div v-else>No Band Selected</div>
                            </div>
                            <div class="truncate" data-cy="exercise-resistance-summary" v-else-if="resistanceSummary">{{ resistanceSummary }}</div>
                            <div class="truncate font-semibold" v-else-if="showMovementName">{{ exercise.movement ? exercise.movement.name : 'Movement' }}</div>
                            <MovementDemoVideoButton v-if="showMovementName" :movement="exercise.movement"/>
                            <span class="shrink-0 rounded-full bg-brand-primary-900 px-2 py-0.5 text-xs text-white" v-if="exercise.advanced">ADV</span>
                        </div>
                        <div class="mt-1 text-xs text-neutral-600 dark:text-neutral-400" data-cy="exercise-exhaustion-level" v-if="exhaustionLevel">
                            Exhaustion: {{ exercise.exhaustionLevel }}
                        </div>
                    </div>

                    <div class="flex shrink-0 items-center gap-x-3">
                        <div v-if="exercise.duration || showPreviousDurationSlot" class="text-right">
                            <div v-if="exercise.duration">
                                <Icon icon="stopwatch"/>
                                {{ $timerValue(exercise.duration) }}
                            </div>
                            <div class="mt-0.5 flex min-h-4 justify-end text-xs text-neutral-600 dark:text-neutral-400"
                                 data-cy="exercise-previous-duration-slot">
                                <span v-if="previousDuration" data-cy="exercise-previous-duration">Prev: {{ previousDuration }}</span>
                                <span v-else-if="lastExerciseLoading"
                                      class="mt-0.5 block h-2.5 w-12 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700"
                                      data-cy="previous-exercise-skeleton"></span>
                            </div>
                        </div>

                        <div v-if="exercise.movement && exercise.movement.trackRepetitions && !sortable" class="text-right">
                            <div class="flex justify-end">
                                <div class="font-semibold">{{ exercise.repetitions || 0 }}</div>
                                <div v-if="exercise.movement.trackRepetitionsPartial">
                                    +{{ exercise.partialRepetitions || 0 }}
                                </div>
                            </div>
                            <div class="mt-0.5 flex min-h-4 justify-end text-xs text-neutral-600 dark:text-neutral-400"
                                 data-cy="exercise-previous-reps-slot">
                                <span v-if="previousRepetitionSummary" data-cy="exercise-previous-reps">Prev: {{ previousRepetitionSummary }}</span>
                                <span v-else-if="lastExerciseLoading"
                                      class="mt-0.5 block h-2.5 w-12 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700"
                                      data-cy="previous-exercise-skeleton"></span>
                            </div>
                        </div>

                        <button type="button"
                                aria-label="Delete set"
                                class="flex h-8 w-8 items-center justify-center rounded border border-neutral-300 text-neutral-500 hover:border-red-700 hover:text-red-700 dark:border-neutral-700 dark:text-neutral-400"
                                data-cy="delete-set"
                                title="Delete set"
                                v-if="removable && !sortable"
                                @click.stop="$emit('delete')">
                            <Icon icon="trash-alt"/>
                        </button>
                    </div>
                </div>

                <Notes :notes="exercise.notes" />
            </template>

            <template v-else>
                <div class="flex items-center justify-between">

                    <div class="flex min-w-0 items-center gap-x-2.5">
                        <div class="font-semibold" v-if="showMovementName">{{ exercise.movement ? exercise.movement.name : 'Movement' }}</div>
                        <MovementDemoVideoButton v-if="showMovementName" :movement="exercise.movement"/>
                        <span class="text-xs bg-brand-primary-900 text-white px-2 py-0.5 rounded-full" v-if="exercise.advanced">ADV</span>
                    </div>

                    <div class="flex items-center gap-x-4">

                        <div v-if="exercise.duration || showPreviousDurationSlot" class="text-right">
                            <div v-if="exercise.duration">
                                <Icon icon="stopwatch"/>
                                {{ $timerValue(exercise.duration) }}
                            </div>
                            <div class="mt-0.5 flex min-h-4 justify-end text-xs text-neutral-600 dark:text-neutral-400"
                                 data-cy="exercise-previous-duration-slot">
                                <span v-if="previousDuration" data-cy="exercise-previous-duration">Prev: {{ previousDuration }}</span>
                                <span v-else-if="lastExerciseLoading"
                                      class="mt-0.5 block h-2.5 w-12 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700"
                                      data-cy="previous-exercise-skeleton"></span>
                            </div>
                        </div>

                        <div v-if="exercise.movement && exercise.movement.trackRepetitions && !sortable" class="text-right">
                            <div class="flex justify-end">
                                <div class="font-semibold">{{ exercise.repetitions || 0 }}</div>
                                <div v-if="exercise.movement.trackRepetitionsPartial">
                                    +{{ exercise.partialRepetitions || 0 }}
                                </div>
                            </div>
                            <div class="mt-0.5 flex min-h-4 justify-end text-xs text-neutral-600 dark:text-neutral-400"
                                 data-cy="exercise-previous-reps-slot">
                                <span v-if="previousRepetitionSummary" data-cy="exercise-previous-reps">Prev: {{ previousRepetitionSummary }}</span>
                                <span v-else-if="lastExerciseLoading"
                                      class="mt-0.5 block h-2.5 w-12 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700"
                                      data-cy="previous-exercise-skeleton"></span>
                            </div>
                        </div>

                    </div>

                </div>

                <div class="flex items-center justify-between" v-if="exercise.movement">

                    <div class="flex gap-x-2.5">

                        <div v-if="exercise.movement.trackResistance">

                            <div data-cy="exercise-resistance-summary" v-if="isBandResistance">
                                <template v-if="exerciseBands.length">
                                    <div data-cy="exercise-resistance-band" v-for="band in exerciseBands" :key="band.id">
                                        {{ band.brandAndName }}
                                    </div>
                                </template>
                                <div v-else>No Band Selected</div>
                            </div>

                            <div v-else-if="weight">
                                {{ weight }}
                            </div>

                        </div>

                    </div>

                </div>

                <div class="mt-2 text-sm text-neutral-600 dark:text-neutral-400" data-cy="exercise-exhaustion-level" v-if="exhaustionLevel">
                    Exhaustion: {{ exercise.exhaustionLevel }}
                </div>

                <Notes :notes="exercise.notes" />
            </template>

        </div>

        <div class="drag-handle rounded border border-neutral-500 px-5 py-2 ml-4" v-if="sortable">
            <Icon icon="sort"/>
        </div>

    </div>
</template>

<script>
import {defineComponent} from 'vue';
import Notes from '@/components/Exercise/Notes.vue';
import MovementDemoVideoButton from '@/components/Movement/DemoVideoButton.vue';

export default defineComponent({
  name: 'Row',
  components: {
    MovementDemoVideoButton,
    Notes,
  },
  emits: [
    'delete',
  ],
  computed: {
    exerciseBands() {
      const bandIds = Array.isArray(this.exercise.bandIds)
        ? this.exercise.bandIds
        : this.exercise.band_ids;

      if (Array.isArray(bandIds)) {
        const bands = this.$store.getters['band/all'] || [];

        return bandIds
          .map(bandId => bands.find(band => String(band.id) === String(bandId)))
          .filter(Boolean);
      }

      return this.exercise.bands || [];
    },
    user() {
      return this.$store.getters['auth/user'];
    },
    weight() {
      if (!this.exercise.weight) {
        return null;
      }

      const displayUnit = this.weightDisplayUnit;
      const displayWeight = this.$convert(this.exercise.weight, 'kg').to(displayUnit);
      const displayFormat = this.user.systemOfMeasure === 'metric' ? '0,0.0' : '0,0';
      return `${this.$numeral(displayWeight).format(displayFormat)} ${displayUnit}`;
    },
    exhaustionLevel() {
      return this.exercise.exhaustionLevel || null;
    },
    isBandResistance() {
      return this.exercise.movement?.trackResistance && this.exercise.movement.resistanceType === 'band';
    },
    resistanceSummary() {
      if (!this.exercise.movement || !this.exercise.movement.trackResistance) {
        return null;
      }

      if (this.exercise.movement.resistanceType === 'band') {
        return this.exerciseBands.length ? this.exerciseBands.map(band => band.brandAndName).join(', ') : 'No Band Selected';
      }

      return this.weight;
    },
    previousRepetitionSummary() {
      if (!this.lastExercise || !this.exercise.movement || !this.exercise.movement.trackRepetitions) {
        return null;
      }

      const repetitions = this.lastExercise.repetitions;
      const partialRepetitions = this.lastExercise.partialRepetitions;
      const hasRepetitions = repetitions !== undefined && repetitions !== null;
      const hasPartialRepetitions = partialRepetitions !== undefined && partialRepetitions !== null
        && this.exercise.movement.trackRepetitionsPartial;

      if (!hasRepetitions && !hasPartialRepetitions) {
        return null;
      }

      if (hasPartialRepetitions) {
        return `${hasRepetitions ? repetitions : 0} +${partialRepetitions}`;
      }

      return repetitions;
    },
    previousDuration() {
      if (!this.user.settings.metaPrevDuration || !this.lastExercise?.duration || !this.exercise.movement?.trackDuration) {
        return null;
      }

      return this.$timerValue(this.lastExercise.duration);
    },
    showPreviousDurationSlot() {
      return Boolean(this.user.settings.metaPrevDuration && this.exercise.movement?.trackDuration);
    },
    weightDisplayUnit() {
      return this.user.systemOfMeasure === 'metric' ? 'kg' : 'lb';
    },
  },
  props: {
    bordered: {
      default: false,
      required: false,
    },
    compact: {
      default: false,
      required: false,
    },
    exercise: {
      required: true,
    },
    lastExercise: {
      default: null,
      required: false,
    },
    lastExerciseLoading: {
      default: false,
      required: false,
      type: Boolean,
    },
    removable: {
      default: false,
      required: false,
      type: Boolean,
    },
    showMovementName: {
      default: true,
      required: false,
    },
    sortable: {
      default: false,
      required: false,
    },
  },
});
</script>

<style scoped lang="scss">

</style>
